import { formatNumber, formatPercent } from '@angular/common';
import { RowType, TrendType } from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { InsightsSummaryDataKpi } from '../../coaching-insight/insight-widget/model/insights-summary-data-kpi.model';
import { KpiType } from '../../coaching-insight/insight-widget/model/kpi-type.model';
import { epicDependencies } from '../../store';
import { CardItem } from '../models/card-item.model';
import { CardRowData } from '../models/card-row-data.model';
import { Mi2DataResult } from '../models/mi2-data-result';
import { TranslationKeyMap } from '../my-coaching-card.i18n';

import { determineTrend } from './determine-trend.function';
import { getHeaderRowData } from './get-header-row-data.function';
import { getProgressDataMi2 } from './get-progress-data-mi2.function';

export function getMi2Item(
    mi2DataResult: Mi2DataResult,
    kpiType: InsightsSummaryDataKpi,
): CardItem | null {
    const dates = mi2DataResult.dates;
    const {
        startDate,
        endDate,
        createdOn = new Date(),
    } = dates ?? { createdOn: new Date() };

    const headerData = getHeaderRowData(startDate, endDate, kpiType.kpiTypeId);
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    const isFlMi2 =
        kpiType.kpiTypeId === KpiType.FL_MANAGEMENT_IMPROVEMENT_INDEX;
    const rowType = isFlMi2 ? RowType.FLMI2 : RowType.MI2;
    const dataRowNameLabel = translateService.instant(
        isFlMi2
            ? TranslationKeyMap._11118_FRONT_LINE_MI2
            : TranslationKeyMap._798_MI2,
    );
    if (
        mi2DataResult == null ||
        Object.keys(mi2DataResult).length === 0 ||
        mi2DataResult?.miScoreDataList.length === 0
    ) {
        return {
            headerData,
            cardRowsData: [
                {
                    rowData: {
                        dataRowNameLabel,
                        rowType,
                        isVisible: true,
                        noDataLabel: translateService.instant(
                            TranslationKeyMap._9866_NO_MI2_DATA_AVAILABLE,
                        ),
                    },
                },
            ],
            type: rowType,
        };
    }

    const progressData = getProgressDataMi2(mi2DataResult, createdOn);
    const { miScoreDataList } = mi2DataResult;
    const [miScoreData] = miScoreDataList;
    const {
        actual: currentActual,
        previous: previousActual,
        thresholdColor,
        target,
        pctToTarget,
    } = miScoreData;

    const { trendId, trendColor } = determineTrend(
        currentActual,
        previousActual,
    );

    const cardRowsData: CardRowData[] = [
        {
            rowData: {
                dataRowNameLabel: kpiType.kpiInitials,
                rowType,
                isVisible: true,
                percentageValue:
                    currentActual != null
                        ? formatPercent(currentActual / 100, 'en-CA', '1.0-0')
                        : '-',
                opportunityColor: thresholdColor,
                placeholderStr:
                    currentActual != null && trendId !== TrendType.UNKNOWN
                        ? null
                        : translateService.instant(TranslationKeyMap._9402_NA),
                menuTooltipData: [
                    {
                        label: translateService.instant(
                            TranslationKeyMap._725_ACTUAL,
                        ),
                        value:
                            currentActual != null
                                ? formatNumber(currentActual, 'en-CA', '1.0-2')
                                : '-',
                    },
                    {
                        label: translateService.instant(
                            TranslationKeyMap._726_TARGET,
                        ),
                        value: formatNumber(target, 'en-CA', '1.0-2'),
                    },
                    {
                        label: translateService.instant(
                            TranslationKeyMap._768___TO_TARGET,
                        ),
                        value: formatPercent(pctToTarget, 'en-CA', '1.0-2'),
                    },
                    {
                        label: translateService.instant(
                            TranslationKeyMap._4105_PREVIOUS_ACTUAL,
                        ),
                        value:
                            previousActual != null
                                ? formatNumber(previousActual, 'en-CA', '1.0-2')
                                : '-',
                    },
                ],
                trendId,
                trendColor,
            },
            progressData,
        },
    ];

    const cardItem: CardItem = { headerData, cardRowsData, type: rowType };
    return cardItem;
}
